import { differenceInSeconds } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
  InlineModal,
  StyledIframe
} from "./ScormWrapper.styles";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore, useNavbarStore } from "../../stores";
import type { LearningActivity, Level } from "../../types";
import { getPreviousRoute } from "../../utils/general";

// Il refresh parte REFRESH_MARGIN_MS prima di exp; MIN_REFRESH_DELAY_MS evita
// loop stretti se exp e' gia' vicino/passato o se l'orologio client e' sfasato.
const REFRESH_MARGIN_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;

export function ScormWrapper({
  activityId,
  close,
  courseId,
  // fullScreen=false,
  isOpen,
  learningObjectTypology,
  learningObjectStarted,
  pathId,
  canAccessNow,
  canAccessUntil,
  setTriggerPause
} : {
  activityId: number
  close?: () => void
  courseId: string | null
  // fullScreen?: boolean
  isOpen: boolean
  learningObjectTypology: Level
  learningObjectStarted?: LearningActivity
  pathId: string | null,
  // aggiunti con il refactoring autoplay
  canAccessNow?: boolean,
  canAccessUntil?: string,
  setTriggerPause?:Dispatch<SetStateAction<boolean>>
}) {

  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Nuovi token letti dalla libreria tracker (aggiornati dal refresh proattivo)
  const tokenRef = useRef<string>("");
  const sessionTokenRef = useRef<string>("");

  const uri = `${window.location.origin}/esplora/scorm/${activityId}/${learningObjectTypology}`;
  const language = useLanguageStore(state => state.language)?.id;

  // il passaggio dei dati tramite questa postMessage mandava status stale E:
  // - Il CTA in dettaglio legge P (giusto).
  // - In watch hai un valore iniziale stale E (cache + refetch in corso).
  // - Quel valore stale viene serializzato e inviato via postMessage.
  // - Nel frame SCORM il dato da postMessage ha priorità e blocca il fetch locale (di fatto non corregge più a P).
  // è stato quindi rimossa la chiamata postMessage per DA_SCORM 2026-05-19
  const shouldUsePostMessageBootstrap = Boolean(learningObjectStarted)
    && learningObjectTypology !== "DA_SCORM";
  const params = `courseId=${courseId || null}&rootId=${pathId || null}&lang=${language}`;
  const navbarItem = useNavbarStore(state => state.item);
  const { pathname }=useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (isOpen) {
      iframeRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (modalRef.current && !document.contains(modalRef.current)) {
        observer.disconnect();
        const previousPath =
          getPreviousRoute({
            courseId: (courseId ? Number(courseId) : undefined),
            id: activityId,
            learningObjectTypology,
            path: pathname,
            rootId: (pathId ? Number(pathId) : undefined),
            section: navbarItem
          });
        navigate(previousPath);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [activityId, learningObjectTypology, courseId, pathId, pathname, navbarItem, navigate]);


  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.data.type === "SCORM_READY") {
        if (
          iframeRef.current
          && iframeRef.current.contentWindow
          && learningObjectStarted
          && shouldUsePostMessageBootstrap
        ) {
          iframeRef.current.contentWindow.postMessage({
            data: learningObjectStarted,
            type: "START_OR_RESUME_DATA"
          }, window.location.origin);
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [learningObjectStarted, shouldUsePostMessageBootstrap]);

  // Refresh del token: gli hook sono esposti sulla main window e letti dalla
  // libreria via window.top. Due trigger, stessa funzione (olRefreshToken):
  // - proattivo: un timer scatta REFRESH_MARGIN_MS prima di exp dell'accessToken
  //   (la scadenza varia per corporate, quindi exp si legge dal token); serve a
  //   riscrivere il cookie digitedUserLO che protegge i media delle slide prima
  //   che scada;
  // - reattivo: la libreria chiama olRefreshToken quando una sua chiamata va in 401.
  // Mentre la lightbox e' aperta i token freschi stanno in ref locali, senza
  // toccare lo store (un set() cambierebbe la queryKey di useStartOrResumeQuery e
  // rimonterebbe la lightbox). Alla chiusura commitQuietRefresh li riporta nello store.
  useEffect(() => {
    if (!isOpen) return;

    const storeToken = () =>
      useAuthStore.getState().session?.getAccessToken().getJwtToken() ?? "";
    const storeSession = () =>
      useAuthStore.getState().sessionToken ?? "";

    tokenRef.current = storeToken();
    sessionTokenRef.current = storeSession();

    let timer: ReturnType<typeof setTimeout> | undefined;
    let refreshInFlight: Promise<string | null> | null = null;
    let disposed = false;
    // exp (secondi epoch) dell'accessToken corrente e ora dell'ultimo tentativo di
    // refresh: servono al controllo su focus/visibilita' (checkExpiry).
    let expiresAtSec = 0;
    let lastAttemptAt = 0;

    // Cookie che protegge il contenuto delle slide: va riscritto a ogni refresh.
    const writeContentCookie = (accessToken: string, sessionId: string) => {
      const date = new Date();
      date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
      document.cookie = `digitedUserLO=${JSON.stringify({ accessToken, sessionId })}`
        + `; expires=${date.toUTCString()}; path=/`;
    };

    // Ritorna il nuovo accessToken se il refresh va a buon fine; null se anche
    // il refreshToken e' scaduto -> la libreria mostra il re-login. Un solo refresh
    // alla volta: timer e 401 della libreria condividono la stessa Promise.
    const refreshToken = (): Promise<string | null> => {
      if (refreshInFlight) return refreshInFlight;
      lastAttemptAt = Date.now();
      refreshInFlight = (async () => {
        const tokens = await useAuthStore.getState().refreshTokensQuiet();
        if (!tokens) return null;
        tokenRef.current = tokens.accessToken;
        sessionTokenRef.current = tokens.sessionToken;
        writeContentCookie(tokens.accessToken, tokens.sessionToken);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        scheduleRefresh(tokens.expiresAt);
        return tokens.accessToken;
      })().finally(() => { refreshInFlight = null });
      return refreshInFlight;
    };

    // Se il refresh fallisce non si riarma il timer: niente retry in loop, restano il
    // refresh reattivo sul 401 della libreria e il controllo su focus/visibilita'.
    function scheduleRefresh(expiresAt: number) {
      if (disposed || !expiresAt) return;
      expiresAtSec = expiresAt;
      clearTimeout(timer);
      const delay = Math.max(expiresAt * 1000 - Date.now() - REFRESH_MARGIN_MS, MIN_REFRESH_DELAY_MS);
      timer = setTimeout(() => { void refreshToken() }, delay);
    }

    // Tab nascosta, PC in sospensione o scheda congelata: il timer puo' scattare in
    // ritardo o non scattare. Al ritorno dell'utente (focus / tab visibile) si
    // ricontrolla exp e si rinnova subito se manca meno del margine, cosi' il cookie
    // dei media e' fresco prima del primo click. MIN_REFRESH_DELAY_MS limita i
    // tentativi ripetuti (es. refreshToken scaduto o rete assente).
    const checkExpiry = () => {
      if (disposed || !expiresAtSec || document.visibilityState === "hidden") return;
      if (expiresAtSec * 1000 - Date.now() > REFRESH_MARGIN_MS) return;
      if (Date.now() - lastAttemptAt < MIN_REFRESH_DELAY_MS) return;
      void refreshToken();
    };
    document.addEventListener("visibilitychange", checkExpiry);
    window.addEventListener("focus", checkExpiry);

    window.olGetToken = () => tokenRef.current || storeToken();
    window.olGetSessionToken = () => sessionTokenRef.current || storeSession();
    window.olRefreshToken = refreshToken;

    scheduleRefresh(useAuthStore.getState().session?.getAccessToken().getExpiration() ?? 0);

    return () => {
      disposed = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkExpiry);
      window.removeEventListener("focus", checkExpiry);
      delete window.olGetToken;
      delete window.olGetSessionToken;
      delete window.olRefreshToken;
      // lightbox chiusa: ora il set() sullo store e' sicuro
      useAuthStore.getState().commitQuietRefresh();
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    // We now primarily rely on SCORM_READY, but we can keep a small delay as fallback
    // if we haven't received SCORM_READY yet.
    // However, the handshake is much safer for non-Chrome browsers.
  };

  //TODO manca il tracciamento alla chiusura dello SCORM su blocco orario
  //_______Logica blocco orario SCORM__________START
  const timeUpdateHandlerScorm = () => {
    if(canAccessNow && canAccessUntil){
      const timeLeft = differenceInSeconds(new Date(canAccessUntil), new Date());
      if(timeLeft <= 0){
        close && close();
        setTriggerPause && setTriggerPause(true);
      }
    }
  };
  //timeUpdateHandlerScorm viene eseguita ogni 250 millisecondi e se il range orarario in cui l'utente può accedere allo
  //scorm è > di new Date verrà mostrato il pop up <LearningActivityBlockOverlay>

  useEffect(() => {

    if (!isOpen || learningObjectTypology !== "DA_SCORM"|| !canAccessNow || !canAccessUntil) return;
    const intervalId = setInterval(() => {
      timeUpdateHandlerScorm();
    }, 250);
    return () => clearInterval(intervalId);
  }, [isOpen,learningObjectTypology,canAccessNow,canAccessUntil]);
  //_______Logica blocco orario SCORM__________END


  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const toRestore: HTMLElement[] = [];
    let node: HTMLElement | null = modalRef.current;

    while (node && node !== document.body) {
      const parent: HTMLElement | null = node.parentElement;
      // I portal MUI (Dialog, Modal, Snackbar, ecc.) vengono montati come figli
      // diretti di document.body: se marcati inert diventano non cliccabili pur
      // restando visibili sopra l'iframe (es. TutorialBox che non si chiude più).
      if (parent && parent !== document.body) {
        Array.from(parent.children).forEach((sibling) => {
          const el = sibling as HTMLElement;
          if (el !== node && !el.hasAttribute("inert")) {
            el.setAttribute("inert", "");
            toRestore.push(el);
          }
        });
      }
      node = parent;
    }

    return () => {
      toRestore.forEach(el => el.removeAttribute("inert"));
    };
  }, [isOpen]);

  return (
    <InlineModal $open={ isOpen } ref={ modalRef }>
      <StyledIframe
        ref={ iframeRef }
        src={ `${uri}?${params}${shouldUsePostMessageBootstrap ? "&hasPostMessageData=true" : ""}` }
        title={ `iframe-scorm-${learningObjectStarted?.title ?? t("learning_activity")}` }
        onLoad={ handleIframeLoad }
      />
    </InlineModal>
  );
}
